import React from 'react';
import { TrendingUp, TrendingDown, Wallet, RefreshCw } from 'lucide-react';

const WalletBalance = ({ balance, pendingBalance, totalCredits, totalDebits, currency, loading, onRefresh }) => {
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })?.format(amount || 0);
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0a1628] via-[#0d1f3c] to-[#1a2f50] p-6 shadow-2xl border border-[#ffd700]/20">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-[#ffd700]/5 rounded-full -translate-y-32 translate-x-32" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#ffd700]/5 rounded-full translate-y-24 -translate-x-24" />

      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#ffd700]/20 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[#ffd700]" />
            </div>
            <div>
              <p className="caption-text text-[#ffd700]/70 uppercase tracking-widest">Available Balance</p>
              <p className="caption-text text-white/40">Computed from ledger</p>
            </div>
          </div>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 text-white/60 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Main Balance */}
        <div className="mb-6">
          {loading ? (
            <div className="h-12 w-48 bg-white/10 rounded-xl animate-pulse" />
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="text-[#ffd700]/60 text-xl font-medium">{currency || 'KES'}</span>
              <span className="text-white text-5xl font-bold tracking-tight">
                {formatAmount(balance)}
              </span>
            </div>
          )}
          {pendingBalance > 0 && (
            <p className="body-text text-white/40 mt-1">
              + {currency || 'KES'} {formatAmount(pendingBalance)} pending (escrow)
            </p>
          )}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white/5 rounded-xl p-3 border border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-white/50 text-xs uppercase tracking-wide">Total In</span>
            </div>
            {loading ? (
              <div className="h-5 w-24 bg-white/10 rounded animate-pulse" />
            ) : (
              <p className="text-emerald-400 font-semibold text-sm">
                {currency || 'KES'} {formatAmount(totalCredits)}
              </p>
            )}
          </div>
          <div className="bg-white/5 rounded-xl p-3 border border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-4 h-4 text-rose-400" />
              <span className="text-white/50 text-xs uppercase tracking-wide">Total Out</span>
            </div>
            {loading ? (
              <div className="h-5 w-24 bg-white/10 rounded animate-pulse" />
            ) : (
              <p className="text-rose-400 font-semibold text-sm">
                {currency || 'KES'} {formatAmount(totalDebits)}
              </p>
            )}
          </div>
        </div>

        {/* Ledger note */}
        <p className="caption-text text-white/20 mt-4 text-center">
          Balance = SUM(credits) − SUM(debits) from completed transactions
        </p>
      </div>
    </div>
  );
};

export default WalletBalance;
